'use client';

import { CSSProperties, ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { css, cx } from 'styled-system/css';
import { button } from 'styled-system/recipes';

type Props = {
  children: ReactNode;
  /** px between cards (also feeds the card width formula via `--svc-gap`). */
  cardGap: number;
  /** px of the next card peeking in on xl (feeds `--svc-peek`). */
  peek: number;
  variant: 'dark' | 'light';
};

// Full-bleed rail: pull out to the viewport edges on both sides (matching SectionContentWrapper's
// gutters) and pad back in — same values as CONTENT_GUTTER / CONTENT_GUTTER_NEGATIVE (literals for
// Panda's static extraction).
const trackClass = css({
  display: 'flex',
  mb: 6,
  gap: 'var(--svc-gap)',
  overflowX: 'auto',
  marginX: {
    base: 'calc(-1 * max(20px, calc((100vw - 1920px) / 2 + 20px)))',
    sm: 'calc(-1 * max(24px, calc((100vw - 1920px) / 2 + 24px)))',
    lg: 'calc(-1 * max(80px, calc((100vw - 1920px) / 2 + 80px)))',
    '3xl': 'calc(-1 * max(96px, calc((100vw - 1920px) / 2 + 96px)))',
  },
  paddingX: {
    base: 'max(20px, calc((100vw - 1920px) / 2 + 20px))',
    sm: 'max(24px, calc((100vw - 1920px) / 2 + 24px))',
    lg: 'max(80px, calc((100vw - 1920px) / 2 + 80px))',
    '3xl': 'max(96px, calc((100vw - 1920px) / 2 + 96px))',
  },
  scrollSnapType: 'x mandatory',
  scrollPaddingLeft: {
    base: 'max(20px, calc((100vw - 1920px) / 2 + 20px))',
    sm: 'max(24px, calc((100vw - 1920px) / 2 + 24px))',
    lg: 'max(80px, calc((100vw - 1920px) / 2 + 80px))',
    '3xl': 'max(96px, calc((100vw - 1920px) / 2 + 96px))',
  },
  scrollPaddingRight: {
    base: 'max(20px, calc((100vw - 1920px) / 2 + 20px))',
    sm: 'max(24px, calc((100vw - 1920px) / 2 + 24px))',
    lg: 'max(80px, calc((100vw - 1920px) / 2 + 80px))',
    '3xl': 'max(96px, calc((100vw - 1920px) / 2 + 96px))',
  },
  overscrollBehaviorX: 'contain',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
});

// SliderControls (functional/SliderControls) without Chakra: Flex end/end + two v2 Buttons
// (`label=""`, icon only, iconSpacing 0) with the sliderLight/sliderDark variants.
const controlsClass = css({ display: 'flex', justifyContent: 'flex-end', alignItems: 'end' });
const controlsDarkClass = css({ '& svg path': { fill: 'white' } });
const arrowClass = css({ display: 'inline-flex', alignSelf: 'center', flexShrink: 0 });
const enabledClass = css({ opacity: 1, cursor: 'pointer' });
const disabledClass = css({ opacity: 0.3, cursor: 'not-allowed' });

const ArrowLeft = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path
      d="M3.828 6.99968H16V8.99968H3.828L9.192 14.3637L7.778 15.7777L0 7.99968L7.778 0.22168L9.192 1.63568L3.828 6.99968Z"
      fill="#5F2DD3"
    />
  </svg>
);

const ArrowRight = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path
      d="M12.172 6.99968L6.808 1.63568L8.222 0.22168L16 7.99968L8.222 15.7777L6.808 14.3637L12.172 8.99968H0V6.99968H12.172Z"
      fill="#5F2DD3"
    />
  </svg>
);

/**
 * Client island of IndustriesServicesSection: the scroll-snap track (cards are server children)
 * + the prev/next controls. Native horizontal scroll, arrows scroll by one card; edge state is
 * measured against the track in a ResizeObserver callback (no eager layout read on hydration).
 * On screens wide enough to fit every card the controls disappear.
 */
export const ServicesRail = ({ children, cardGap, peek, variant }: Props) => {
  const isLight = variant === 'light';
  const trackRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [canScroll, setCanScroll] = useState(true);

  const updateEdges = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    // The trailing padding is the see-through gutter, not content — subtract it from scrollWidth.
    const paddingRight = parseFloat(getComputedStyle(track).paddingRight) || 0;
    const contentEnd = track.scrollWidth - paddingRight;
    setCanScroll(contentEnd > track.clientWidth + 1);
    setAtStart(track.scrollLeft <= 1);
    setAtEnd(track.scrollLeft + track.clientWidth >= contentEnd - 1);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    if (typeof ResizeObserver === 'undefined') {
      updateEdges();
      window.addEventListener('resize', updateEdges);
      return () => window.removeEventListener('resize', updateEdges);
    }
    const observer = new ResizeObserver(() => updateEdges());
    observer.observe(track);
    return () => observer.disconnect();
  }, [updateEdges]);

  const scrollByCard = (direction: 1 | -1) => {
    const track = trackRef.current;
    const card = track?.firstElementChild as HTMLElement | null;
    if (!track || !card) return;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    track.scrollBy({ left: direction * (card.offsetWidth + cardGap), behavior });
  };

  const buttonClass = button({ variant: isLight ? 'sliderLight' : 'sliderDark' });

  return (
    <>
      <div
        ref={trackRef}
        onScroll={updateEdges}
        className={trackClass}
        style={{ '--svc-gap': `${cardGap}px`, '--svc-peek': `${peek}px` } as CSSProperties}
      >
        {children}
      </div>

      {canScroll && (
        <div className={cx(controlsClass, !isLight && controlsDarkClass)}>
          <button
            type="button"
            aria-label="previous slide"
            onClick={() => scrollByCard(-1)}
            disabled={atStart}
            className={cx(buttonClass, atStart ? disabledClass : enabledClass)}
          >
            <span className={arrowClass}>
              <ArrowLeft />
            </span>
          </button>
          <button
            type="button"
            aria-label="next slide"
            onClick={() => scrollByCard(1)}
            disabled={atEnd}
            className={cx(buttonClass, css({ ml: 4 }), atEnd ? disabledClass : enabledClass)}
          >
            <span className={arrowClass}>
              <ArrowRight />
            </span>
          </button>
        </div>
      )}
    </>
  );
};
